import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface TrendData {
  period: string;
  value: number;
  change_percentage: number;
  trend_direction: 'up' | 'down' | 'stable';
}

interface TrendsChartProps {
  data: TrendData[];
  title: string;
  color?: string;
  height?: number;
}

const TrendsChart: React.FC<TrendsChartProps> = ({ 
  data, 
  title, 
  color = '#3b82f6',
  height = 300 
}) => {
  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatValue = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(1);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`Period: ${label}`}</p>
          <p className="text-blue-600">
            {`Value: ${formatValue(data.value)}`}
          </p>
          <p className={`${data.change_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {`Change: ${data.change_percentage.toFixed(1)}%`}
          </p>
          <div className="flex items-center gap-1 mt-1">
            {getTrendIcon(data.trend_direction)}
            <span className="text-sm capitalize">{data.trend_direction}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
          <span className="text-sm text-gray-600">Trend</span>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="period" 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatValue}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: color, strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {data.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Latest Value</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatValue(data[data.length - 1]?.value || 0)}
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Average</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatValue(data.reduce((sum, item) => sum + item.value, 0) / data.length)}
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Trend</p>
            <div className="flex items-center justify-center gap-1">
              {getTrendIcon(data[data.length - 1]?.trend_direction || 'stable')}
              <span className="text-sm capitalize">
                {data[data.length - 1]?.trend_direction || 'stable'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendsChart;


